import React from "react";
import { trpc } from "@/utils/trpc";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
	FileText,
	Users,
	Calendar,
	Lock,
	Globe,
	Plus,
	FolderOpen,
	Edit3,
	Clock,
} from "lucide-react";

export interface DocsOverviewProps {
	workspaceId: string;
	session?: any;
	currentWorkspace?: any;
}

export const DocsOverview: React.FC<DocsOverviewProps> = ({
	workspaceId,
	session,
	currentWorkspace,
}) => {
	const {
		isPending,
		error,
		data: channelData,
	} = useQuery(
		trpc.channel.getByWorkspace.queryOptions({
			workspaceId: Number(workspaceId),
		}),
	);

	if (isPending) {
		return (
			<div className="flex h-full items-center justify-center">
				<div className="text-center">
					<div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-primary border-b-2" />
					<p className="text-muted-foreground">Loading documents...</p>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex h-full items-center justify-center">
				<div className="rounded-lg border border-border bg-destructive/10 p-8 text-center">
					<FileText className="mx-auto mb-4 h-12 w-12 text-destructive" />
					<h3 className="mb-2 font-semibold text-destructive text-lg">
						Error loading documents
					</h3>
					<p className="text-destructive/80">{error.message}</p>
				</div>
			</div>
		);
	}

	// Sample document data for demonstration
	const enhancedDocs = channelData?.map((channel: any) => ({
		...channel,
		documentCount: Math.floor(Math.random() * 20) + 1,
		lastModified: new Date(
			Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000,
		).toISOString(),
		collaborators: Math.floor(Math.random() * 8) + 1,
		isShared: Math.random() > 0.5,
		documentType: [
			"Technical Spec",
			"Meeting Notes",
			"Project Plan",
			"Documentation",
			"Guide",
		][Math.floor(Math.random() * 5)],
		description: `Documents and notes for ${channel.name}`,
	}));

	return (
		<div className="flex h-full flex-col bg-background">
			{/* Header */}
			<div className="border-border border-b bg-card px-6 py-4">
				<div className="flex items-center justify-between">
					<div>
						<h1 className="font-bold text-2xl text-foreground">Documents</h1>
						<p className="mt-1 text-muted-foreground text-sm">
							Access and manage your team's documents
						</p>
					</div>
					<button className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90">
						<Plus className="h-4 w-4" />
						New Document
					</button>
				</div>
			</div>

			{/* Scrollable Grid Container */}
			<div className="flex-1 overflow-y-auto p-6">
				{enhancedDocs && enhancedDocs.length > 0 ? (
					<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
						{enhancedDocs.map((doc: any) => (
							<Link
								key={doc.id}
								to="/$workspaceId/docs/$channelId"
								params={{ workspaceId: workspaceId, channelId: doc.id }}
								className="group"
							>
								<div className="flex h-full cursor-pointer flex-col rounded-lg border border-border bg-card p-5 text-card-foreground transition-all duration-200 hover:border-accent hover:shadow-lg">
									{/* Document Header */}
									<div className="mb-3 flex items-start justify-between">
										<div className="rounded-lg bg-accent p-2 transition-colors group-hover:bg-accent/80">
											<FileText className="h-5 w-5 text-accent-foreground" />
										</div>
										{doc.isShared && (
											<span className="rounded-full bg-chart-2/20 px-2 py-1 font-medium text-chart-2 text-xs">
												Shared
											</span>
										)}
									</div>

									{/* Document Info */}
									<h3 className="mb-1 font-semibold text-card-foreground transition-colors group-hover:text-primary">
										{doc.name}
									</h3>
									<span className="mb-2 text-muted-foreground text-xs">
										{doc.documentType}
									</span>
									<p className="mb-4 line-clamp-2 flex-1 text-muted-foreground text-sm">
										{doc.description}
									</p>

									{/* Document Stats */}
									<div className="space-y-2 border-border border-t pt-3">
										<div className="flex items-center justify-between text-muted-foreground text-xs">
											<div className="flex items-center gap-1">
												<FolderOpen className="h-3 w-3" />
												<span>{doc.documentCount} docs</span>
											</div>
											<div className="flex items-center gap-1">
												<Users className="h-3 w-3" />
												<span>{doc.collaborators}</span>
											</div>
										</div>
										<div className="flex items-center gap-1 text-muted-foreground text-xs">
											<Clock className="h-3 w-3" />
											<span>
												Modified{" "}
												{new Date(doc.lastModified).toLocaleDateString(
													undefined,
													{
														month: "short",
														day: "numeric",
													},
												)}
											</span>
										</div>
									</div>
								</div>
							</Link>
						))}
					</div>
				) : (
					<div className="flex h-full flex-col items-center justify-center">
						<div className="max-w-md rounded-lg border border-border bg-card p-8 text-center">
							<FileText className="mx-auto mb-4 h-16 w-16 text-muted-foreground" />
							<h3 className="mb-2 font-semibold text-foreground text-lg">
								No documents yet
							</h3>
							<p className="mb-4 text-muted-foreground">
								Start creating and organizing your team's documents.
							</p>
							<button className="rounded-lg bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90">
								Create First Document
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};